import React, { Fragment, useCallback, useMemo, useState } from "react";
import BinIcon from "../../components/icon/Icon";
import _ from "lodash";
import {
    RequestFeedbackLogoStyled,
    RequestFeedbackStarsStyled,
    RequestFeedbackStyled
} from "./styled/RequestFeedbackStyled";
import { useLangContext } from "../../providers/LangProvider/LangProviderContext";
import { Button, Form, FormGroup } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { sendRequestFeedbackAction } from "../../app/appActions";
import { isClientAndroid, isClientIOS } from "../../components/ClientInfo/ClientInfo";
import { getAppStoreLinkApple, getAppStoreLinkGoogle } from "../../components/ToAppButton/toAppSelectors";
import { getAppFeatures, getRequestFeedbackSent } from "../../app/appSelectors";
import { FEATURES } from "../../app/appConstants";
import { placeholderMap } from './constants';

interface IRequestFeedbackProps {
    isPopup?: boolean,
    onClose?: () => void,
    onSkip?: () => void,
}

export default function RequestFeedback(props: IRequestFeedbackProps) {
    const { onSkip, onClose, isPopup } = props;
    const { getLangPath } = useLangContext();
    const [stars, setStars] = useState(0);
    const [needFeedback] = useState(false);
    const feedbackRef = React.createRef<HTMLTextAreaElement>();
    const dispatch = useDispatch();
    const features = useSelector(getAppFeatures);

    const appStoreLinkApple = useSelector(getAppStoreLinkApple);
    const appStoreLinkGoogle = useSelector(getAppStoreLinkGoogle);
    const requestFeedbackSent = useSelector(getRequestFeedbackSent);

    const link = useMemo(() => isClientIOS
        ? appStoreLinkApple
        : appStoreLinkGoogle, [appStoreLinkApple, appStoreLinkGoogle]);

    const clickStarHandler = useCallback((e) => {
        e.preventDefault();
        const index = e.currentTarget.dataset.index;
        setStars(Number(index));
    }, []);

    const submitHandler = useCallback((e) => {
        e.preventDefault();
        dispatch(sendRequestFeedbackAction({
            comment: feedbackRef.current?.value,
            device: isClientIOS ? 'ios' : isClientAndroid ? 'android' : 'web',
            rating: stars,
            source: isPopup ? 'popup' : 'page',
        }));
        if (stars >= 4 && isClientAndroid && features[FEATURES.REQUEST_FEEDBACK_APPSTORE]) {
            window.open(link);
        } else if (stars >= 4) {
            window.open(`https://www.trustpilot.com/evaluate/jetvix.com?stars=${stars}`);
            // window.open(`https://www.facebook.com/jetvixcom/reviews`);
        }
        onClose?.();
    }, [dispatch, onClose, feedbackRef, features, link, isPopup, stars]);

    const skipHandler = useCallback((e) => {
        e.preventDefault();
        onSkip?.();
        onClose?.()
    }, [onClose, onSkip]);

    if (requestFeedbackSent) {
        return <RequestFeedbackStyled>
            <h5>{getLangPath('request_feedback.title_thanks')}</h5>
            <div className={'f14 text-secondary'}>{getLangPath('request_feedback.subtitle_thanks')}</div>
        </RequestFeedbackStyled>
    }

    return <RequestFeedbackStyled>
        {
            isPopup && <RequestFeedbackLogoStyled>
                <BinIcon name={'IcLogoLetter'} size={'24px'}/>
            </RequestFeedbackLogoStyled>
        }
        <h5>{getLangPath(needFeedback ? 'request_feedback.title_matter' : 'request_feedback.title_enjoy')}</h5>
        <p className={'f14 text-secondary'}>{getLangPath(needFeedback ? 'request_feedback.subtitle_matter' : 'request_feedback.subtitle')}</p>
        <RequestFeedbackStarsStyled>
            {
                _.range(1, 6).map((item) => <Fragment key={item}>
                    <BinIcon name={stars >= item ? 'IcStarFilled' : 'IcStar'}
                             style={{ fill: `var(${stars >= item ? '' : '--color-label-primary'})` }}
                             className={'cursor-pointer'}
                             data-index={item}
                             onClick={clickStarHandler}/>
                </Fragment>)
            }
        </RequestFeedbackStarsStyled>
        <FormGroup className={'mt-3'}>
            <Form.Control
                as='textarea'
                name='payment_info'
                placeholder={getLangPath(placeholderMap.get(stars))}
                ref={feedbackRef}
            />
        </FormGroup>
        <FormGroup className={'mt-4'}>
            {
                (isPopup && !stars) &&
                <Button variant="secondary"
                        onClick={skipHandler}>{getLangPath('request_feedback.controls.skip')}</Button>
            }
            {
                (!isPopup || !!stars) &&
                <Button variant="primary"
                        onClick={submitHandler}
                        disabled={!stars}>{getLangPath(needFeedback ? 'request_feedback.controls.send' : 'request_feedback.controls.submit')}</Button>
            }
        </FormGroup>
    </RequestFeedbackStyled>
}
